// src/components/BalancingForecastPanel.jsx
//
// Phase 1 (price-forecast expansion): the GR balancing forecast panel for the
// Market -> Energy prices tab. Shows the latest PUBLISHED P10/P50/P90 per
// series (reserve capacity price x {aFRR,mFRR,FCR} x {Up,Down} + imbalance)
// for one market-local delivery day, plus the recommended P50 with its model
// label when the reviewed backtest recommends a baseline over the GBM
// ("honest models or none" - the recommendation is data, shown, never
// implied). Self-contained: fetches /balancing/forecast/latest itself.
//
// p24: it also draws the REALISED ACTUAL of the selected series as a second
// line, with the stored score, from /balancing/forecast/vs-actual. Two rules
// govern that overlay. It is merged by delivery_start, never by array index -
// an actual shifted against its forecast looks exactly like forecast error,
// and that is a mistake this project has already made once. And the actual
// line does NOT connect nulls: a missing interval is a break in the line,
// because bridging it would draw a segment that measures nothing. Reserve
// actuals land the evening before delivery; GR imbalance arrives in weekly
// ENTSO-E batches 8-17 days later, so "not yet available" is a normal state
// and is labelled as one rather than left to look like a fault.
//
// Theme: MUI tokens only for chrome (text.secondary, divider, background via
// Paper); fixed series hexes for chart marks, matching the house convention
// of the other market panels, chosen to read on both light and dark.
import React, { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import {
  Area,
  ComposedChart,
  CartesianGrid,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  fetchBalancingForecastLatest,
  fetchBalancingForecastVsActual,
} from "../services/api";
import PublicationNote from "./PublicationNote";
import MarketInfoDialog from "./MarketInfoDialog";
import AppDatePicker from "./common/AppDatePicker";
import { chartTooltipProps } from "./common/chartTooltipStyles";

const SERIES_OPTIONS = [
  { key: "afrr_up", label: "aFRR Up" },
  { key: "afrr_down", label: "aFRR Down" },
  { key: "mfrr_up", label: "mFRR Up" },
  { key: "mfrr_down", label: "mFRR Down" },
  { key: "fcr_up", label: "FCR Up" },
  { key: "fcr_down", label: "FCR Down" },
  { key: "imbalance", label: "Imbalance" },
];

const UNIT_LABELS = {
  EUR_PER_MW_PER_INTERVAL: "EUR/MW per 15-min interval",
  EUR_PER_MWH: "EUR/MWh",
};

const COLORS = {
  band: "#1565c0",
  p50: "#1565c0",
  recommended: "#ef6c00",
  actual: "#2e7d32",
};

// What the backend says about the actual's availability, in the panel's words.
// severity "info" throughout for the absent cases: a reserve day before its
// auction, or an imbalance day inside its 8-17 day batch window, is waiting -
// not failing.
const ACTUAL_STATE_LABELS = {
  actual_available: { label: "actual: complete", color: "success" },
  actual_partial: { label: "actual: partial", color: "warning" },
  actual_not_yet_available: { label: "actual: not yet available", color: "default" },
  no_published_forecast: { label: "no published run", color: "default" },
};

function defaultDeliveryDate() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

export default function BalancingForecastPanel({ market }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [deliveryDate, setDeliveryDate] = useState(defaultDeliveryDate);
  const [seriesKey, setSeriesKey] = useState("afrr_up");
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // The actual/score read is kept in its own state with its own error so a
  // failure here can never blank the forecast the panel already had.
  const [vsActual, setVsActual] = useState(null);
  const [vsActualError, setVsActualError] = useState(null);

  useEffect(() => {
    if (!market || !deliveryDate) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchBalancingForecastLatest({ market, deliveryDate })
      .then((data) => {
        if (!cancelled) setPayload(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setPayload(null);
          setError(err?.message || "Failed to load balancing forecast");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [market, deliveryDate]);

  // Series-scoped on purpose: /vs-actual costs four queries per series, and
  // the panel only ever draws one at a time.
  useEffect(() => {
    if (!market || !deliveryDate || !seriesKey) return undefined;
    let cancelled = false;
    setVsActual(null);
    setVsActualError(null);
    fetchBalancingForecastVsActual({ market, deliveryDate, seriesKey })
      .then((data) => {
        if (!cancelled) setVsActual(data?.series?.[seriesKey] || null);
      })
      .catch((err) => {
        if (!cancelled) {
          setVsActual(null);
          setVsActualError(err?.message || "Failed to load the realised actual");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [market, deliveryDate, seriesKey]);

  const seriesData = payload?.series?.[seriesKey] || null;
  const publishedSeriesKeys = Object.keys(payload?.series || {});

  // Keyed on delivery_start, NOT zipped by index. See this file's header.
  const actualByStart = useMemo(() => {
    const map = new Map();
    (vsActual?.rows || []).forEach((row) => {
      if (row?.delivery_start) map.set(row.delivery_start, row.actual);
    });
    return map;
  }, [vsActual]);

  const chartData = useMemo(() => {
    if (!seriesData?.rows?.length) return [];
    return seriesData.rows.map((row) => ({
      time: (row.delivery_local_time || "").slice(0, 5),
      band: [row.p10, row.p90],
      p50: row.p50,
      recommended: row.recommended_p50,
      // undefined when this interval was not in the actual payload at all,
      // null when it was there without a value. Recharts breaks the line for
      // both, which is the intended reading: no measurement here.
      actual: actualByStart.has(row.delivery_start)
        ? actualByStart.get(row.delivery_start)
        : undefined,
    }));
  }, [seriesData, actualByStart]);

  // p29: a baseline-published series can publish its P50 with NO band, on a
  // day the GBM could not be computed. Read from the payload, never inferred
  // from the rows here - the backend already decided and said so.
  const bandAvailable = seriesData?.band_available !== false;
  const bandNote = seriesData?.band_note || null;

  const hasActual = (vsActual?.n_periods_with_actual || 0) > 0;
  const actualStateChip = ACTUAL_STATE_LABELS[vsActual?.state] || null;
  const score = vsActual?.score || null;

  const recommendedModel = seriesData?.recommended_model || null;
  const recommendedIsBaseline = recommendedModel && recommendedModel !== "gbm";
  const unitLabel = UNIT_LABELS[seriesData?.price_unit] || seriesData?.price_unit || "";

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 3, mt: 3 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Balancing Forecast
          </Typography>
          {!bandAvailable && (
            <Chip
              size="small"
              variant="outlined"
              color="warning"
              label="P50 only — no band"
              title={bandNote || ""}
            />
          )}
          {recommendedModel && (
            <Chip
              size="small"
              variant="outlined"
              color={recommendedIsBaseline ? "warning" : "primary"}
              label={
                recommendedIsBaseline
                  ? `recommended: ${recommendedModel} (baseline)`
                  : "recommended: GBM"
              }
            />
          )}
        </Stack>
        {/* Was <TextField type="date" />, which renders the browser-native
            picker - a different control from the one every other dashboard
            uses, and locale-dependent (a Greek user was shown mm/dd/yyyy).
            AppDatePicker keeps the same "YYYY-MM-DD" string contract, so the
            state and the handler are untouched; only the element changes. */}
        <AppDatePicker
          label="Delivery day"
          value={deliveryDate}
          onChange={setDeliveryDate}
          sx={{ minWidth: 170 }}
        />
      </Stack>
      <Stack direction="row" spacing={0.5} alignItems="flex-start">
        <PublicationNote product="balancing_forecast" market={market} />
        <MarketInfoDialog market={market} />
      </Stack>
      <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" sx={{ mb: 1.25 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={seriesKey}
          onChange={(_event, value) => {
            if (value) setSeriesKey(value);
          }}
        >
          {SERIES_OPTIONS.map((option) => (
            <ToggleButton
              key={option.key}
              value={option.key}
              disabled={payload != null && !publishedSeriesKeys.includes(option.key)}
            >
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>
      {!bandAvailable && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {bandNote || "No P10–P90 band was published for this series and day."}
        </Alert>
      )}
      {(actualStateChip || score) && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1.25 }}>
          {actualStateChip && (
            <Chip
              size="small"
              variant="outlined"
              color={actualStateChip.color}
              label={
                vsActual?.state === "actual_partial" || vsActual?.state === "actual_available"
                  ? `${actualStateChip.label} (${vsActual.n_periods_with_actual}/${vsActual.n_periods})`
                  : actualStateChip.label
              }
              title={vsActual?.state_reason || ""}
            />
          )}
          {score && (
            <Chip
              size="small"
              variant="outlined"
              color="primary"
              label={`MAE (P50) ${Number(score.mae_p50).toFixed(2)}`}
              title={`Stored score, computed once when enough actuals existed (append-only). n=${score.n_periods_scored}/${score.n_periods}. Unit as published: ${score.price_unit}.`}
            />
          )}
          {score?.p10_p90_coverage_pct != null && (
            <Chip
              size="small"
              variant="outlined"
              label={`P10-P90 coverage ${Number(score.p10_p90_coverage_pct).toFixed(0)}%`}
              title="Share of intervals whose actual fell inside the calibrated band. Target 80%."
            />
          )}
          {/* Scoring is append-only and graded whichever run was latest AT
              SCORING TIME. If a newer run was published afterwards, the chart
              is that newer run and this MAE is not its MAE. Said out loud. */}
          {score && score.is_for_the_run_shown === false && (
            <Chip
              size="small"
              variant="outlined"
              color="warning"
              label="score is for an earlier run"
              title="The score was computed for a different (earlier) published run of this series and day; the chart shows the latest run. Scores are append-only and never recomputed."
            />
          )}
          {vsActual?.score_state === "not_yet_scored" && hasActual && (
            <Chip
              size="small"
              variant="outlined"
              label="not yet scored"
              title="Actuals exist but the append-only scorer has not graded this day yet."
            />
          )}
        </Stack>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {vsActualError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          The forecast below is unaffected, but the realised actual could not be loaded:{" "}
          {vsActualError}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ height: 360, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress size={28} />
        </Box>
      ) : !seriesData || chartData.length === 0 ? (
        <Alert severity="info">
          No published balancing forecast for {market} on {deliveryDate}
          {payload?.missing_series?.length === SERIES_OPTIONS.length
            ? " (no series published for this day - the forecast runs ~15:30 market-local on D-1)"
            : ` for this series${publishedSeriesKeys.length ? ` - published: ${publishedSeriesKeys.join(", ")}` : ""}`}
          .
        </Alert>
      ) : (
        <>
          <Box sx={{ height: 360 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 8, right: 16, bottom: 4, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.35} />
                <XAxis dataKey="time" minTickGap={28} tick={{ fontSize: 12 }} />
                <YAxis
                  tick={{ fontSize: 12 }}
                  width={64}
                  label={{
                    value: unitLabel,
                    angle: -90,
                    position: "insideLeft",
                    style: { fontSize: 11 },
                  }}
                />
                <Tooltip
                  {...chartTooltipProps(isDark)}
                  formatter={(value, name) => {
                    if (Array.isArray(value)) {
                      return [`${Number(value[0]).toFixed(2)} – ${Number(value[1]).toFixed(2)}`, "P10–P90"];
                    }
                    return [Number(value).toFixed(2), name];
                  }}
                  labelFormatter={(label) => `Delivery ${deliveryDate} ${label} (market-local)`}
                />
                <Legend />
                {/* NOT rendered when no band was published. An Area over
                    null pairs draws a flat sliver on the axis, which reads as
                    a zero-width band - perfect certainty - and that is the
                    most misleading thing this panel could show. */}
                {bandAvailable && (
                  <Area
                    type="monotone"
                    dataKey="band"
                    name="P10–P90 (GBM+CQR)"
                    stroke="none"
                    fill={COLORS.band}
                    fillOpacity={0.14}
                    isAnimationActive={false}
                    connectNulls
                  />
                )}
                {/* On a band-less day the GBM's P50 is null as well. The line
                    that remains is the recommended one below, which is drawn
                    precisely when the recommendation is a baseline - the case
                    that produces such a day. */}
                {bandAvailable && (
                  <Line
                    type="monotone"
                    dataKey="p50"
                    name="P50 (GBM)"
                    stroke={COLORS.p50}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                )}
                {hasActual && (
                  <Line
                    type="monotone"
                    dataKey="actual"
                    name="Actual (realised)"
                    stroke={COLORS.actual}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                    // NOT connectNulls: a gap in the actuals must read as a
                    // gap, not as a straight line through intervals nobody
                    // measured.
                    connectNulls={false}
                  />
                )}
                {/* The only price line on a band-less day. recommendedIsBaseline
                    is true exactly when the series publishes a baseline, which
                    is the only way a band-less day can arise. */}
                {recommendedIsBaseline && (
                  <Line
                    type="monotone"
                    dataKey="recommended"
                    name={`P50 (recommended: ${recommendedModel})`}
                    stroke={COLORS.recommended}
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    dot={false}
                    isAnimationActive={false}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.75 }}>
            {recommendedIsBaseline
              ? `Per the reviewed walk-forward backtest, the ${recommendedModel} baseline beats the GBM for this ` +
                "series and is the recommended P50; the shaded band is the GBM's calibrated uncertainty, shown for context."
              : "GBM P50 with split-CQR calibrated P10–P90 band (target coverage 80%), per the reviewed walk-forward backtest."}
            {seriesData?.run_at ? ` Published ${new Date(seriesData.run_at).toLocaleString("en-GB")}.` : ""}
            {vsActual?.state_reason ? ` Actual: ${vsActual.state_reason}.` : ""}
          </Typography>
        </>
      )}
    </Paper>
  );
}
